<template>
  <div>
    <div class="max-w-xl mx-auto px-8 my-8 text-teal-900">
      <div
        class="bg-teal-100 border-t-4 border-teal-500 rounded-b text-teal-900 px-4 py-3 my-4 shadow-md"
        role="alert"
      >
        <div class="flex">
          <div class="py-1">
            <svg
              class="fill-current h-6 w-6 text-teal-500 mr-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
            >
              <path
                d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
              />
            </svg>
          </div>
          <div>
            <p class="font-bold text-lg">Thank you for your purchase!</p>
            <p class="text-sm">Your photos are below.</p>
          </div>
        </div>
      </div>
      <div
        class="bg-white overflow-hidden shadow rounded-lg my-5 p-8"
      >
        <h2 class="text-xl font-bold text-teal-800">Your Rafting Photos</h2>
        <p class="text-gray-600 mt-1">
          Had a great time whitewater rafting with {{ outfitter.name }}
        </p>
        <p class="text-sm text-gray-500 mt-3">
          Hover over a photo and click the download icon to save, or use the
          zip download below.
        </p>

        <div
          v-if="outfitter.trip_advisor || outfitter.google"
          class="mt-6 pt-6 border-t border-gray-200"
        >
          <p class="text-sm font-medium text-gray-700 mb-3">Enjoyed your trip? Leave a review</p>
          <div class="flex flex-wrap gap-3">
            <a
              v-if="outfitter.trip_advisor"
              :href="makeLink(outfitter.trip_advisor)"
              target="_blank"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 shadow-sm"
            >
              <img
                class="mr-2 h-4 w-4"
                src="@/assets/icons/tripadvisor.svg"
              />
              Trip Advisor
            </a>
            <a
              v-if="outfitter.google"
              :href="makeLink(outfitter.google)"
              target="_blank"
              class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 shadow-sm"
            >
              <img
                class="mr-2 h-4 w-4"
                src="@/assets/icons/google.svg"
              />
              Google
            </a>
          </div>
        </div>

        <div class="mt-6 pt-6 border-t border-gray-200">
          <div v-if="zipDownloadUrl">
            <a
              class="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 shadow-sm"
              target="_blank"
              :href="zipDownloadUrl"
            >
              <font-awesome-icon icon="download" class="mr-2" />
              Download Zip File
            </a>
            <p class="text-xs text-gray-500 mt-2">
              If your browser blocks this download, right click the button and
              select "Save link as..."
            </p>
          </div>
          <div v-else-if="zipBuilding" class="flex items-center text-sm text-gray-600">
            <svg class="animate-spin h-5 w-5 text-teal-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Building your zip file...
          </div>
          <div v-else-if="zipFailed" class="text-sm text-gray-600">
            There was a problem building your zip file.
            <span class="text-teal-600 cursor-pointer hover:text-teal-700 font-medium" @click="initiateZip"
              >Try again</span
            >
          </div>
          <div v-else class="text-sm text-gray-600">
            Want all your photos in one file?
            <span class="text-teal-600 cursor-pointer hover:text-teal-700 font-medium" @click="initiateZip"
              >Download as zip</span
            >
          </div>
        </div>
      </div>
    </div>

    <div
      class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 px-5 pb-8 max-w-6xl mx-auto"
    >
      <div
        v-for="(photo, index) in photos"
        :key="photo.id"
        class="photo-card rounded-lg overflow-hidden shadow-md cursor-pointer"
        @mouseenter="hoveredIndex = index"
        @mouseleave="hoveredIndex = null"
        @click="downloadPhoto(photo, index)"
      >
        <img
          :src="photo.delivery_path"
          class="w-full h-full object-cover"
          :class="{ 'photo-dimmed': hoveredIndex === index }"
        />
        <div
          class="photo-overlay"
          :class="{ 'photo-overlay-visible': hoveredIndex === index }"
        >
          <div v-if="downloadingIndex === index" class="text-center">
            <font-awesome-icon
              icon="spinner"
              spin
              class="text-white text-3xl"
            />
            <div class="text-white text-sm mt-2">Downloading...</div>
          </div>
          <div v-else class="text-center">
            <font-awesome-icon
              icon="download"
              class="text-white text-3xl"
            />
            <div class="text-white text-sm mt-2">Download</div>
          </div>
        </div>
      </div>
    </div>

    <base-modal
      :show="showModal"
      @close="showModal = false"
      :showButtons="false"
    >
      <template v-slot:header>
        Leave A Review
        <div class="sm:block absolute top-0 right-0 pt-4 pr-4">
          <button
            type="button"
            class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            @click="overrideModal = true"
          >
            X
          </button>
        </div>
      </template>
      <template v-slot:main>
        <div class="grid grid-cols-2 gap-8">
          <div class="text-center col-span-2">
            Had a great time? Let the world know!
          </div>
          <div
            class="text-center"
            v-if="outfitter.trip_advisor"
            :class="{ 'col-span-2': !outfitter.google }"
          >
            <a :href="makeLink(outfitter.trip_advisor)" target="_blank">
              <button
                type="button"
                class="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <img
                  class="-ml-1 mr-3 h-5 w-5"
                  src="@/assets/icons/tripadvisor.svg"
                />
                Trip Advisor
              </button>
            </a>
          </div>
          <div
            class="text-center"
            v-if="outfitter.google"
            :class="{ 'col-span-2': !outfitter.trip_advisor }"
          >
            <a :href="makeLink(outfitter.google)" target="_blank">
              <button
                type="button"
                class="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <img
                  class="-ml-1 mr-3 h-5 w-5"
                  src="@/assets/icons/google.svg"
                />
                Google
              </button>
            </a>
          </div>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script>
export default {
  name: "Order",

  data() {
    return {
      overrideModal: false,
      zipBuilding: false,
      zipFailed: false,
      zipDownloadUrl: null,
      zipPollTimer: null,
      hoveredIndex: null,
      downloadingIndex: null,
    };
  },
  methods: {
    async initiateZip() {
      this.zipBuilding = true;
      this.zipFailed = false;
      this.zipDownloadUrl = null;

      const resp = await this.$store.dispatch("order/zip", this.order.id);
      if (resp && resp.status === "completed" && resp.download_url) {
        this.zipBuilding = false;
        this.zipDownloadUrl = resp.download_url;
        return;
      }

      this.startPolling();
    },
    startPolling() {
      this.stopPolling();
      this.zipPollTimer = setInterval(() => this.pollZipStatus(), 3000);
    },
    stopPolling() {
      if (this.zipPollTimer) {
        clearInterval(this.zipPollTimer);
        this.zipPollTimer = null;
      }
    },
    async pollZipStatus() {
      const resp = await this.$store.dispatch("order/zipStatus", this.order.id);
      if (!resp || !resp.success) return;

      if (resp.status === "completed") {
        this.stopPolling();
        this.zipBuilding = false;
        this.zipDownloadUrl = resp.download_url;
      } else if (resp.status === "failed") {
        this.stopPolling();
        this.zipBuilding = false;
        this.zipFailed = true;
      }
    },
    async checkZipStatus() {
      const resp = await this.$store.dispatch("order/zipStatus", this.order.id);
      if (!resp || !resp.success) return;

      if (resp.status === "completed" && resp.download_url) {
        this.zipDownloadUrl = resp.download_url;
      } else if (resp.status === "pending" || resp.status === "processing") {
        this.zipBuilding = true;
        this.startPolling();
      } else if (resp.status === "failed") {
        this.zipFailed = true;
      }
    },
    makeLink(link) {
      return "https://" + link.replace("https://", "");
    },
    async downloadPhoto(photo, index) {
      if (this.downloadingIndex !== null) return;
      this.downloadingIndex = index;
      try {
        const hash = this.$route.params.orderHash;
        const url =
          process.env.VUE_APP_BASE_URL +
          "/order/download-photo/" +
          hash +
          "/" +
          photo.id;
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = photo.file_name || "photo.jpg";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(photo.delivery_path, "_blank");
      } finally {
        this.downloadingIndex = null;
      }
    },
  },
  computed: {
    showModal: function () {
      if (
        !this.overrideModal &&
        this.outfitter &&
        (this.outfitter.google || this.outfitter.trip_advisor)
      ) {
        return true;
      }
      return false;
    },
    outfitter: function () {
      return this.$store.getters["search/getOutfitter"];
    },
    order: function () {
      return this.$store.getters["order/getOrder"];
    },
    photos: function () {
      let photos = [];
      this.order.order_items.forEach((order_item) => {
        if (order_item.photo) {
          photos.push(order_item.photo);
        } else if (order_item.display_group) {
          order_item.display_group.photos.forEach((photo) => {
            photos.push(photo);
          });
        }
      });
      return photos;
    },
    isExternal: function () {
      return this.$store.getters["search/stateIsExternal"];
    },
  },
  watch: {
    "order.id": function (newId) {
      if (newId) {
        this.checkZipStatus();
      }
    },
  },
  mounted() {
    this.$store.dispatch("order/load_order_hash", this.$route.params.orderHash);
    localStorage.removeItem("wwp-orderId");
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped>
.photo-card {
  position: relative;
  aspect-ratio: 4 / 3;
  background-color: #e2e8f0;
}

.photo-card img {
  transition: opacity 0.2s ease;
}

.photo-dimmed {
  opacity: 0.7;
}

.photo-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.photo-overlay-visible {
  opacity: 1;
}

@media (hover: none) {
  .photo-overlay {
    opacity: 0.5;
  }

  .photo-card img {
    opacity: 0.85;
  }
}
</style>
